/**
 * Helpers de listagem a partir dos MedicationDispense.
 * validUntil = extension valueDate; recorded = emissão.
 * @module purchase-release/listHelpers
 */

import type { MedicationDispense } from "fhir/r5";

import {
  PURCHASE_RELEASE_BATCH_EXTENSION_URL,
  PURCHASE_RELEASE_TRANSACTION_ID_EXTENSION_URL,
  PURCHASE_RELEASE_VALID_UNTIL_EXTENSION_URL,
} from "./constants";
import type {
  LocalPurchaseReleaseBatch,
  PurchaseReleaseListItem,
} from "./types";

export function getDispenseProductName(dispense: MedicationDispense): string {
  return (
    dispense.medication?.concept?.text ||
    dispense.medication?.concept?.coding?.[0]?.display ||
    "Produto"
  );
}

/** validUntil = extension valueDate (não whenHandedOver / statusChanged). */
export function getDispenseValidUntil(
  dispense: MedicationDispense,
): string | undefined {
  return dispense.extension?.find(
    (ext) => ext.url === PURCHASE_RELEASE_VALID_UNTIL_EXTENSION_URL,
  )?.valueDate;
}

export function getDispenseBatchId(
  dispense: MedicationDispense,
): string | undefined {
  return dispense.extension?.find(
    (ext) => ext.url === PURCHASE_RELEASE_BATCH_EXTENSION_URL,
  )?.valueString;
}

export function getDispenseTransactionId(
  dispense: MedicationDispense,
): string | undefined {
  return dispense.extension?.find(
    (ext) => ext.url === PURCHASE_RELEASE_TRANSACTION_ID_EXTENSION_URL,
  )?.valueString?.trim();
}

export function getDispenseQuantity(dispense: MedicationDispense): number {
  return dispense.quantity?.value ?? 0;
}

/** recorded = emissão; fallback whenPrepared / meta para sort. */
export function getDispenseRecordedAt(
  dispense: MedicationDispense,
): string | undefined {
  return (
    dispense.recorded ||
    dispense.whenPrepared ||
    dispense.meta?.lastUpdated ||
    undefined
  );
}

export function isActivePurchaseDispense(
  dispense: MedicationDispense,
): boolean {
  return dispense.status !== "cancelled";
}

/** Converte lotes em itens de lista (linguagem leiga). */
export function toPurchaseReleaseListItems(
  batches: LocalPurchaseReleaseBatch[],
): PurchaseReleaseListItem[] {
  return [...batches]
    .map((batch) => ({
      ...batch,
      dispenses: batch.dispenses.filter(isActivePurchaseDispense),
    }))
    .filter((batch) => batch.dispenses.length > 0)
    .sort(
      (a, b) => Date.parse(b.createdAt) - Date.parse(a.createdAt),
    )
    .map((batch) => {
      const names = batch.dispenses.map(getDispenseProductName);
      const validDates = batch.dispenses
        .map(getDispenseValidUntil)
        .filter((d): d is string => Boolean(d))
        .sort();

      return {
        batchId: batch.batchId,
        createdAt: batch.createdAt,
        storeName: batch.storeName,
        productCount: batch.dispenses.length,
        productNames: names,
        validUntil: validDates[0],
        statusLabel: "Liberado",
      };
    });
}
